import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import commonActions from '../../Actions/commonActions';
import priceSimulationActions from '../../Actions/priceSimulationActions';
import useModal from '../../Hooks/useModal';
import { useAppDispatch } from '../../Store/types';
import { roundToTwoDecimals, sumRounded, formatTwoDecimals, formatPercent } from '../../Utils/decimalUtils';
import { InfoLabel } from '../Badges/InfoLabel';
import EmptyState from '../Fundamentals/EmptyState/EmptyState';
import SpirisIcon from '../Fundamentals/Icon/SpirisIcon';
import DatePicker from '../Fundamentals/DatePicker';
import ComboBox from '../Fundamentals/ComboBox';
import {
    BaseModal as BaseModalJS,
    ModalBody as ModalBodyJS,
    ModalFooter as ModalFooterJS,
    ModalHeader as ModalHeaderJS,
    ModalHeaderTitle as ModalHeaderTitleJS,
    ModalSize,
} from '../Fundamentals/Modal/StandardModal';
import { extractDraftModifications, toSavedModification } from './proposalPayloadExtractor';
import type { PackageProposalPayload, ProposalModification } from './proposalPayloadExtractor';
import type { Adjustments, RootState } from './PriceSimulation.types';
import {
    forEachPricePoint,
    hasSavedKept,
    isDraftApproval,
    isListedForApproval,
    isTiered,
    PRICING_SCHEME,
    resolvePlanChange,
    toPricePointKey,
    toTierKey,
} from './priceSimulationModel';
import type { PackageSimulationDto, TierDto, TieredPricePointDto } from './priceSimulationModel';
import type { components } from '../../Types/api';

type ReviewerDto = components['schemas']['Cockpit.Models.PriceSimulation.ReviewerDto'];

interface DraftProduct {
    productKey: string;
    productId?: string;
    productName?: string | null;
    groupName?: string | null;
    planIdentifier: string;
    currentPrice: number;
    newPrice: number;
    adjustmentPct: number;
    tierId?: string;
    groupId?: string;
    pricingScheme: number;
    tieredPrices: TieredPricePointDto[];
    isKept: boolean;
}

interface DraftTier {
    tierKey: string;
    tierId?: string;
    tierName?: string | null;
    products: DraftProduct[];
}

interface DraftPackageGroup {
    packageId: string;
    packageName?: string | null;
    tiers: DraftTier[];
}

interface SendPriceForApprovalModalProps {
    adjustments?: Adjustments;
    selectedPackageIds?: string[];
    onSubmitted?: (packages: PackageProposalPayload[]) => void;
}

type ChildrenOnly = ComponentType<{ children: ReactNode }>;
const BaseModal = BaseModalJS as unknown as ComponentType<{ size?: string; isLoading?: boolean; children: ReactNode }>;
const ModalBody = ModalBodyJS as unknown as ChildrenOnly;
const ModalFooter = ModalFooterJS as unknown as ChildrenOnly;
const ModalHeader = ModalHeaderJS as unknown as ComponentType<{ onclose: () => void; children: ReactNode }>;
const ModalHeaderTitle = ModalHeaderTitleJS as unknown as ChildrenOnly;

interface IndeterminateCheckboxProps {
    id: string;
    checked: boolean;
    indeterminate: boolean;
    onChange: () => void;
}

const IndeterminateCheckbox = ({ id, checked, indeterminate, onChange }: IndeterminateCheckboxProps) => {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return <input ref={ref} id={id} type="checkbox" checked={checked} onChange={onChange} />;
};

const SendPriceForApprovalModal = ({ adjustments = {}, selectedPackageIds = [], onSubmitted }: SendPriceForApprovalModalProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const baseId = useId();

    const { closeModal } = useModal();

    const packageDetailsMap = useSelector((state: RootState) => state.PriceSimulationReducer.packageDetailsMap);
    const allReviewers = useSelector((state: RootState) => state.PriceSimulationReducer.reviewers);
    const currentUserId = useSelector((state: RootState) => state.CommonReducer.user?.id);
    const reviewers = useMemo(() => allReviewers.filter((reviewer) => reviewer.id !== currentUserId), [allReviewers, currentUserId]);
    const isSubmitting = useSelector((state: RootState) => state.CommonReducer.dataLoading.submitApproval === true);

    const [selectedProductKeys, setSelectedProductKeys] = useState<Set<string>>(() => new Set());
    const [selectedReviewerIds, setSelectedReviewerIds] = useState<number[]>([]);
    const [notes, setNotes] = useState('');
    const [publishDate, setPublishDate] = useState('');
    const [expandedPackages, setExpandedPackages] = useState<Set<string>>(() => new Set());
    const [expandedTiers, setExpandedTiers] = useState<Set<string>>(() => new Set());

    useEffect(() => {
        void dispatch(priceSimulationActions.fetchReviewers());
    }, [dispatch]);

    const draftPackageGroups = useMemo<DraftPackageGroup[]>(() => {
        const groups: DraftPackageGroup[] = [];

        for (const packageId of selectedPackageIds) {
            const pkg: PackageSimulationDto | undefined = packageDetailsMap[packageId];
            if (!pkg?.tiers) continue;

            const tiers = new Map<TierDto, DraftTier>();
            forEachPricePoint(pkg, packageId, ({ tier, group, product, planIdentifier, pricePoint, productKey, planKey, pricePointKey }) => {
                const change = resolvePlanChange(pricePoint, adjustments[productKey]?.[planKey]);
                if (!isListedForApproval(pricePoint, change)) return;

                if (!tiers.has(tier)) tiers.set(tier, { tierKey: toTierKey(packageId, tier.tierId), tierId: tier.tierId, tierName: tier.name, products: [] });
                tiers.get(tier)?.products.push({
                    productKey: pricePointKey,
                    productId: product.productId,
                    productName: product.name,
                    groupName: group.name,
                    planIdentifier,
                    currentPrice: change.currentPrice,
                    newPrice: change.newPrice,
                    adjustmentPct: change.adjustmentPercentage,
                    tierId: tier.tierId,
                    groupId: group.groupId,
                    pricingScheme: pricePoint.pricingScheme ?? PRICING_SCHEME.Flat,
                    tieredPrices: pricePoint.tieredPrices ?? [],
                    isKept: change.isKept,
                });
            });

            if (tiers.size > 0) {
                groups.push({ packageId, packageName: pkg.packageName, tiers: [...tiers.values()] });
            }
        }

        return groups;
    }, [packageDetailsMap, selectedPackageIds, adjustments]);

    const allDraftProducts = useMemo(() => draftPackageGroups.flatMap((g) => g.tiers.flatMap((tierItem) => tierItem.products)), [draftPackageGroups]);

    const selectionInitializedRef = useRef(false);

    useEffect(() => {
        if (!selectionInitializedRef.current) {
            if (allDraftProducts.length === 0) return;
            selectionInitializedRef.current = true;
            setSelectedProductKeys(new Set(allDraftProducts.map((p) => p.productKey)));
            setExpandedPackages(new Set(draftPackageGroups.map((g) => g.packageId)));
            setExpandedTiers(new Set(draftPackageGroups.flatMap((g) => g.tiers.map((tierItem) => tierItem.tierKey))));
            return;
        }

        const listedKeys = new Set(allDraftProducts.map((p) => p.productKey));
        setSelectedProductKeys((prev) => {
            const pruned = new Set([...prev].filter((k) => listedKeys.has(k)));
            return pruned.size === prev.size ? prev : pruned;
        });
    }, [allDraftProducts, draftPackageGroups]);

    const selectedCount = allDraftProducts.filter((p) => selectedProductKeys.has(p.productKey)).length;
    const allSelected = allDraftProducts.length > 0 && selectedCount === allDraftProducts.length;

    const handleSelectAll = useCallback(() => {
        setSelectedProductKeys(allSelected ? new Set() : new Set(allDraftProducts.map((p) => p.productKey)));
    }, [allSelected, allDraftProducts]);

    const handleToggleProduct = useCallback((productKey: string) => {
        setSelectedProductKeys((prev) => {
            const next = new Set(prev);
            if (next.has(productKey)) next.delete(productKey);
            else next.add(productKey);
            return next;
        });
    }, []);

    const toggleSelection = useCallback((products: DraftProduct[]) => {
        const keys = products.map((p) => p.productKey);
        setSelectedProductKeys((prev) => {
            const allIn = keys.every((k) => prev.has(k));
            const next = new Set(prev);
            if (allIn) {
                keys.forEach((k) => next.delete(k));
            } else {
                keys.forEach((k) => next.add(k));
            }
            return next;
        });
    }, []);

    const handleTogglePackageExpand = useCallback((packageId: string) => {
        setExpandedPackages((prev) => {
            const next = new Set(prev);
            if (next.has(packageId)) next.delete(packageId);
            else next.add(packageId);
            return next;
        });
    }, []);

    const handleToggleTierExpand = useCallback((tierKey: string) => {
        setExpandedTiers((prev) => {
            const next = new Set(prev);
            if (next.has(tierKey)) next.delete(tierKey);
            else next.add(tierKey);
            return next;
        });
    }, []);

    const totalRevenueImpact = useMemo(
        () => sumRounded(allDraftProducts.filter((p) => selectedProductKeys.has(p.productKey)).map((p) => roundToTwoDecimals(p.newPrice) - roundToTwoDecimals(p.currentPrice))),
        [selectedProductKeys, allDraftProducts]
    );

    const canSubmit = selectedCount > 0 && selectedReviewerIds.length > 0 && !isSubmitting;

    const handleSubmit = () => {
        if (!canSubmit) return;

        const unsavedPayloads = extractDraftModifications(packageDetailsMap, selectedPackageIds, adjustments);
        const packages: PackageProposalPayload[] = [];

        for (const packageId of selectedPackageIds) {
            const pkg: PackageSimulationDto | undefined = packageDetailsMap[packageId];
            if (!pkg?.tiers) continue;

            let modifications: ProposalModification[] = [];

            forEachPricePoint(pkg, packageId, (ref) => {
                if (!selectedProductKeys.has(ref.pricePointKey)) return;
                if ((ref.pricePoint.isModified === true || hasSavedKept(ref.pricePoint)) && isDraftApproval(ref.pricePoint.approvalStatus))
                    modifications.push(toSavedModification(ref));
            });

            const unsavedPkg = unsavedPayloads.find((p) => p.packageId === packageId);
            if (unsavedPkg) {
                const keyOf = (m: ProposalModification) => toPricePointKey(packageId, m.TierId, m.ProductGroupId, m.ProductId, m.PlanIdentifier);
                const unsavedKeys = new Set(unsavedPkg.modifications.map(keyOf));
                const filtered = modifications.filter((m) => !unsavedKeys.has(keyOf(m)));
                const selectedUnsaved = unsavedPkg.modifications.filter((m) => selectedProductKeys.has(keyOf(m)));
                filtered.push(...selectedUnsaved);
                modifications = filtered;
            }

            if (modifications.length > 0) {
                packages.push({
                    packageId,
                    versionId: pkg.versionId,
                    modifications,
                });
            }
        }

        if (packages.length === 0) return;

        Promise.resolve(
            dispatch(
                priceSimulationActions.submitForApproval({
                    packages,
                    reviewerIds: selectedReviewerIds,
                    publishDate: publishDate || null,
                    notes: notes || null,
                })
            )
        )
            .then(() => {
                onSubmitted?.(packages);
                dispatch(commonActions.showSuccess(t('priceSimulation.approvalSubmitted')));
                closeModal();
            })
            .catch(() => {
                dispatch(commonActions.showError(t('priceSimulation.approvalSubmitFailed')));
            });
    };

    const getCheckState = (products: DraftProduct[]) => {
        const count = products.filter((p) => selectedProductKeys.has(p.productKey)).length;
        if (count === 0) return { checked: false, indeterminate: false };
        if (count === products.length) return { checked: true, indeterminate: false };
        return { checked: false, indeterminate: true };
    };

    const handleReviewersChange = (_values: unknown, options: ReviewerDto[]) => {
        setSelectedReviewerIds(options.map((o) => o.id).filter((id): id is number => id !== undefined));
    };

    const formatAdjustmentPct = (pct: number) => `${pct > 0 ? '+' : ''}${formatPercent(pct)}`;

    return (
        <BaseModal size={ModalSize.X_LARGE} isLoading={isSubmitting}>
            <ModalHeader onclose={closeModal}>
                <ModalHeaderTitle>{t('priceSimulation.approvalModalTitle')}</ModalHeaderTitle>
            </ModalHeader>

            <ModalBody>
                <div className="d-flex justify-content-between align-items-center gap-16 mb-8">
                    <p className="text-secondary mb-0">{t('priceSimulation.approvalModalSubtitle')}</p>
                    {allDraftProducts.length > 0 && (
                        <button type="button" className="btn btn-tertiary btn-sm flex-shrink-0" onClick={handleSelectAll}>
                            {allSelected ? t('priceSimulation.deselectAll') : t('priceSimulation.selectAll')}
                        </button>
                    )}
                </div>
                {allDraftProducts.length === 0 ? (
                    <EmptyState size="sm" title={t('priceSimulation.noDraftItems')} />
                ) : (
                    <>
                        <div className="d-flex flex-column gap-8 mb-16">
                            {draftPackageGroups.map((group, groupIndex) => {
                                const packageProducts = group.tiers.flatMap((tierItem) => tierItem.products);
                                const pkgCheck = getCheckState(packageProducts);
                                const isPackageExpanded = expandedPackages.has(group.packageId);
                                const packageDomId = `${baseId}-p${groupIndex}`;

                                return (
                                    <div key={group.packageId} className={`collapse-list border rounded-4 overflow-hidden${isPackageExpanded ? ' open' : ''}`}>
                                        <div className="collapse-list-header d-flex align-items-center gap-8 px-16 py-10">
                                            <div className={`checkbox m-0${pkgCheck.indeterminate ? ' mixed' : ''}`}>
                                                <IndeterminateCheckbox
                                                    id={`${packageDomId}-check`}
                                                    checked={pkgCheck.checked}
                                                    indeterminate={pkgCheck.indeterminate}
                                                    onChange={() => toggleSelection(packageProducts)}
                                                />
                                                <label htmlFor={`${packageDomId}-check`}>
                                                    <span className="sr-only">{group.packageName}</span>
                                                </label>
                                            </div>
                                            <button
                                                type="button"
                                                className="collapse-list-title bg-transparent p-0 gap-8"
                                                aria-expanded={isPackageExpanded}
                                                aria-controls={`${packageDomId}-content`}
                                                onClick={() => handleTogglePackageExpand(group.packageId)}
                                            >
                                                <SpirisIcon name="article" size="sm" />
                                                <span className="d-flex align-items-center gap-8 flex-1 min-w-0">
                                                    <span className="body-sm bold">{group.packageName}</span>
                                                    <span className="text-secondary body-xs">{t('priceSimulation.itemCount', { count: packageProducts.length })}</span>
                                                </span>
                                                <span className="caret" />
                                            </button>
                                        </div>

                                        <div id={`${packageDomId}-content`} className="collapse-list-content p-0">
                                            {group.tiers.map((tier, tierIndex) => {
                                                const tierCheck = getCheckState(tier.products);
                                                const isTierExpanded = expandedTiers.has(tier.tierKey);
                                                const tierDomId = `${packageDomId}-t${tierIndex}`;

                                                return (
                                                    <div key={tier.tierKey} className={`collapse-list border-top${isTierExpanded ? ' open' : ''}`}>
                                                        <div className="collapse-list-header d-flex align-items-center gap-8 py-10 pr-16 pl-40">
                                                            <div className={`checkbox m-0${tierCheck.indeterminate ? ' mixed' : ''}`}>
                                                                <IndeterminateCheckbox
                                                                    id={`${tierDomId}-check`}
                                                                    checked={tierCheck.checked}
                                                                    indeterminate={tierCheck.indeterminate}
                                                                    onChange={() => toggleSelection(tier.products)}
                                                                />
                                                                <label htmlFor={`${tierDomId}-check`}>
                                                                    <span className="sr-only">{tier.tierName}</span>
                                                                </label>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="collapse-list-title bg-transparent p-0 gap-8"
                                                                aria-expanded={isTierExpanded}
                                                                aria-controls={`${tierDomId}-content`}
                                                                onClick={() => handleToggleTierExpand(tier.tierKey)}
                                                            >
                                                                <span className="d-flex align-items-center gap-8 flex-1 min-w-0">
                                                                    <span className="body-sm bold">{tier.tierName}</span>
                                                                    <span className="text-secondary body-xs">
                                                                        {t('priceSimulation.productCount', { count: tier.products.length })}
                                                                    </span>
                                                                </span>
                                                                <span className="caret" />
                                                            </button>
                                                        </div>

                                                        <div id={`${tierDomId}-content`} className="collapse-list-content p-0">
                                                            {tier.products.map((product, productIndex) => {
                                                                const productDomId = `${tierDomId}-${productIndex}`;

                                                                return (
                                                                    <div key={product.productKey} className="d-flex align-items-center gap-12 py-10 pr-16 pl-64 border-top">
                                                                        <div className="checkbox m-0">
                                                                            <input
                                                                                id={productDomId}
                                                                                type="checkbox"
                                                                                checked={selectedProductKeys.has(product.productKey)}
                                                                                onChange={() => handleToggleProduct(product.productKey)}
                                                                            />
                                                                            <label htmlFor={productDomId} className="cursorpointer" />
                                                                        </div>
                                                                        <label
                                                                            htmlFor={productDomId}
                                                                            className="d-flex align-items-center gap-12 flex-1 min-w-0 m-0 font-weight-normal cursorpointer"
                                                                        >
                                                                            <span className="d-flex flex-column gap-2 flex-1 min-w-0">
                                                                                <span>
                                                                                    {product.productName} ({product.planIdentifier})
                                                                                    {Boolean(product.groupName) && (
                                                                                        <span className="text-secondary body-xs"> &bull; {product.groupName}</span>
                                                                                    )}
                                                                                </span>
                                                                                <span className="text-secondary body-sm">
                                                                                    {product.isKept && product.newPrice === product.currentPrice
                                                                                        ? `${formatTwoDecimals(product.currentPrice)} SEK`
                                                                                        : `${formatTwoDecimals(product.currentPrice)} SEK \u2192 ${formatTwoDecimals(product.newPrice)} SEK`}
                                                                                    {isTiered(product) && ` (${t('priceSimulation.volumePricing')})`}
                                                                                </span>
                                                                            </span>
                                                                            {product.isKept && <InfoLabel type="secondary" title={t('priceSimulation.kept')} />}
                                                                            {product.adjustmentPct !== 0 && (
                                                                                <span
                                                                                    className={`body-sm-bold text-nowrap flex-shrink-0 ${product.adjustmentPct > 0 ? 'text-success' : 'text-error'}`}
                                                                                >
                                                                                    {formatAdjustmentPct(product.adjustmentPct)}
                                                                                </span>
                                                                            )}
                                                                        </label>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="approval-summary d-flex gap-32 p-16 rounded-4 mb-24">
                            <div className="d-flex flex-column gap-4">
                                <span className="text-secondary body-xs">{t('priceSimulation.productsSelected')}</span>
                                <span className="heading-md">{selectedCount}</span>
                            </div>
                            <div className="d-flex flex-column gap-4">
                                <span className="text-secondary body-xs">{t('priceSimulation.expectedRevenueImpact')}</span>
                                <span className={`heading-md ${totalRevenueImpact >= 0 ? 'text-success' : 'text-error'}`}>
                                    {totalRevenueImpact >= 0 ? '+' : ''}
                                    {formatTwoDecimals(totalRevenueImpact)} SEK
                                </span>
                            </div>
                        </div>
                    </>
                )}

                <div className="form-group">
                    <ComboBox
                        id={`${baseId}-reviewers`}
                        label={t('priceSimulation.selectReviewers')}
                        required={true}
                        options={reviewers}
                        value={selectedReviewerIds}
                        onChange={handleReviewersChange}
                        isMulti={true}
                        valueKey="id"
                        labelKey="name"
                        placeholder={t('priceSimulation.reviewersPlaceholder')}
                        strategy="dialog"
                    />
                </div>

                <div className="form-group">
                    <DatePicker
                        label={t('priceSimulation.publishingDate')}
                        value={publishDate}
                        onChange={(value) => setPublishDate(value)}
                        showTime={false}
                        placeholderText={t('priceSimulation.publishingDatePlaceholder') ?? undefined}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor={`${baseId}-notes`}>{t('priceSimulation.additionalNotes')}</label>
                    <textarea
                        id={`${baseId}-notes`}
                        className="form-control"
                        rows={4}
                        value={notes}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
                        placeholder={t('priceSimulation.notesPlaceholder') ?? undefined}
                    />
                </div>
            </ModalBody>

            <ModalFooter>
                <div className="w-100 d-flex justify-content-end gap-8">
                    <button type="button" className="btn btn-primary btn-sm mw-100" onClick={handleSubmit} disabled={!canSubmit}>
                        {t('priceSimulation.sendXForApproval', { count: selectedCount })}
                    </button>
                    <button type="button" className="btn btn-sm" onClick={closeModal}>
                        {t('baseModal.cancel')}
                    </button>
                </div>
            </ModalFooter>
        </BaseModal>
    );
};

export default SendPriceForApprovalModal;
